/**
 * Admin User Details - Detailed user view with tabs
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  ChevronLeft, Save, Trash2, Shield, Check, Key, Loader2,
  Mail, Phone, MapPin, Calendar, Briefcase, Star, CreditCard
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUserDetails = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [userJobs, setUserJobs] = useState([]);
  const [userBids, setUserBids] = useState([]);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const res = await axios.get(`${API}/admin/users?search=${userId}`, { withCredentials: true });
      const foundUser = res.data.users?.find(u => u.user_id === userId);
      if (foundUser) {
        setUser(foundUser);
        setFormData(foundUser);
      }
      
      // Fetch user's jobs if client
      if (foundUser?.role === 'client') {
        const jobsRes = await axios.get(`${API}/admin/jobs?client_id=${userId}`, { withCredentials: true });
        setUserJobs(jobsRes.data.jobs || []);
      }
    } catch (error) {
      toast.error('Fehler beim Laden des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${userId}`, formData, { withCredentials: true });
      toast.success('Benutzer aktualisiert');
      setUser(formData);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const verifyUser = async () => {
    try {
      await axios.post(`${API}/admin/verify-user/${userId}`, {}, { withCredentials: true });
      toast.success('Benutzer verifiziert!');
      setUser({ ...user, verified: true });
      setFormData({ ...formData, verified: true });
    } catch (error) {
      toast.error('Fehler beim Verifizieren');
    }
  };

  const makeAdmin = async () => {
    try {
      await axios.post(`${API}/admin/make-admin/${userId}`, {}, { withCredentials: true });
      toast.success('Admin-Rechte gewährt');
      setUser({ ...user, is_admin: true });
      setFormData({ ...formData, is_admin: true });
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const resetPassword = async () => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { withCredentials: true });
      toast.success('Passwort-Reset-Link gesendet');
    } catch (error) {
      toast.error('Fehler beim Passwort-Reset');
    }
  };

  const deleteUser = async () => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('Benutzer gelöscht');
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen infirmly-dark flex items-center justify-center bg-gray-100" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p>Benutzer nicht gefunden</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-user-details">
      {/* Header */}
      <header className="border-b border-white/10 sticky bg-white/[0.04] backdrop-blur top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/users')} className="rounded-full">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Benutzerdetails
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#0044CC] hover:bg-[#003399] rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
            <Button variant="destructive" onClick={deleteUser} className="rounded-full">
              <Trash2 className="w-4 h-4 mr-2" /> Löschen
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* User Header Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={user.profile_picture} />
                <AvatarFallback className="bg-[#0044CC] text-white text-3xl">
                  {user.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold">{user.name}</h2>
                  {user.verified && <Badge className="bg-green-500">Verifiziert</Badge>}
                  {user.is_admin && <Badge className="bg-purple-500">Admin</Badge>}
                  <Badge variant={user.role === 'provider' ? 'default' : 'secondary'}>
                    {user.role === 'provider' ? 'Dienstleister' : 'Kunde'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {user.email}</p>
                  {user.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {user.phone}</p>}
                  {user.location?.city && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {user.location.city}</p>}
                  <p className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Registriert: {new Date(user.created_at).toLocaleDateString('de-CH')}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {!user.verified && user.role === 'provider' && (
                  <Button onClick={verifyUser} className="bg-green-600 hover:bg-green-700 rounded-full">
                    <Check className="w-4 h-4 mr-2" /> Verifizieren
                  </Button>
                )}
                {!user.is_admin && (
                  <Button variant="outline" onClick={makeAdmin} className="rounded-full">
                    <Shield className="w-4 h-4 mr-2" /> Admin machen
                  </Button>
                )}
                <Button variant="outline" onClick={resetPassword} className="rounded-full">
                  <Key className="w-4 h-4 mr-2" /> Passwort zurücksetzen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList>
            <TabsTrigger value="profile">Profil</TabsTrigger>
            <TabsTrigger value="subscription">Abonnement</TabsTrigger>
            {user.role === 'client' && <TabsTrigger value="jobs">Aufträge</TabsTrigger>}
            {user.role === 'provider' && <TabsTrigger value="bids">Gebote</TabsTrigger>}
          </TabsList>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profildaten bearbeiten</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>E-Mail</Label>
                    <Input
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Stadt</Label>
                    <Input
                      value={formData.location?.city || ''}
                      onChange={(e) => setFormData({ ...formData, location: { ...formData.location, city: e.target.value } })}
                      className="mt-1"
                    />
                  </div>
                </div>
                {user.role === 'provider' && (
                  <>
                    <Separator />
                    <div>
                      <Label>Firma</Label>
                      <Input
                        value={formData.company_name || ''}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Abonnement-Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Status</p>
                      <p className="text-sm text-gray-500">Aktueller Abo-Status</p>
                    </div>
                    <Badge className={user.subscription ? 'bg-green-500' : 'bg-gray-500'}>
                      {user.subscription || 'Kein Abo'}
                    </Badge>
                  </div>
                  {user.subscription_expires && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">Gültig bis</p>
                        <p className="text-sm text-gray-500">Ablaufdatum</p>
                      </div>
                      <span>{new Date(user.subscription_expires).toLocaleDateString('de-CH')}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  Aufträge des Benutzers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {userJobs.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">Keine Aufträge gefunden</p>
                ) : (
                  <div className="divide-y">
                    {userJobs.map((job) => (
                      <div key={job.job_id} className="py-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{job.title}</p>
                          <p className="text-sm text-gray-500">{job.category}</p>
                        </div>
                        <Badge>{job.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bids">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Gebote des Dienstleisters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center py-8 text-gray-500">Gebote werden geladen...</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminUserDetails;
